import { useState } from "react";
import { PieChart as PieChartIcon, Play, Loader2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function CommitmentPacing() {
  const { orgId, familyId } = useTenant();
  const queryClient = useQueryClient();
  const [running, setRunning] = useState(false);

  const { data: pacing, isLoading } = useQuery({
    queryKey: ["commitment-pacing", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return null;

      let query = supabase
        .from("commitment_pacing")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (familyId) query = query.eq("family_id", familyId);

      const { data, error } = await query;
      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!orgId,
  });

  const runPacing = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("run-commitment-pacing", {
        body: { orgId, familyId },
      });
      if (error) throw error;
      toast.success(`Pacing analysis complete. Coverage ratio: ${data?.coverage_ratio?.toFixed(2) || "N/A"}x`);
      queryClient.invalidateQueries({ queryKey: ["commitment-pacing"] });
    } catch (err) {
      toast.error("Failed to run pacing analysis: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setRunning(false);
    }
  };

  const quarterlyProjections = (pacing?.quarterly_projections as Array<{
    quarter: string;
    projected_calls: number;
    projected_distributions: number;
    net_flow: number;
  }>) || [];

  if (isLoading) return <div className="flex-1 p-8">Loading...</div>;

  return (
    <div className="flex-1 space-y-6 px-4 py-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Commitment Pacing</h1>
          <p className="text-muted-foreground">Model unfunded commitments against available liquidity</p>
        </div>
        <Button onClick={runPacing} disabled={running}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
          {running ? "Analyzing..." : "Run Analysis"}
        </Button>
      </div>

      {pacing && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Total Unfunded</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">${(pacing.total_unfunded || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Total Committed</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">${(pacing.total_committed || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Available Liquidity</CardTitle></CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">${(pacing.available_liquidity || 0).toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">Coverage Ratio</CardTitle></CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${(pacing.coverage_ratio || 0) < 1.5 ? "text-amber-600" : "text-green-600"}`}>
                  {pacing.coverage_ratio ? `${pacing.coverage_ratio.toFixed(2)}x` : "N/A"}
                </div>
                {(pacing.coverage_ratio || 0) < 1.5 && (
                  <Badge variant="destructive" className="mt-1">Low Coverage</Badge>
                )}
              </CardContent>
            </Card>
          </div>

          {pacing.projected_shortfall_quarter && (
            <Card className="border-destructive">
              <CardContent className="flex items-center gap-3 py-4">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">Projected liquidity shortfall in {pacing.projected_shortfall_quarter}</p>
                  <p className="text-sm text-muted-foreground">Current liquidity may be insufficient to meet projected capital calls.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {quarterlyProjections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Quarterly Projections</CardTitle>
                <CardDescription>Projected calls vs distributions over next 8 quarters</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={quarterlyProjections}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="quarter" />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                      <Legend />
                      <Bar dataKey="projected_calls" name="Capital Calls" fill="hsl(var(--destructive))" />
                      <Bar dataKey="projected_distributions" name="Distributions" fill="hsl(142, 76%, 36%)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!pacing && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <PieChartIcon className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No pacing analysis generated yet.</p>
            <p className="text-sm text-muted-foreground mt-2">Click "Run Analysis" to model your commitment pacing.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
